/**
 * Minimap — canvas overlay showing a small viewport indicator over the world.
 *
 * Reads camera state each frame to draw the viewport rectangle.
 * No PIXI dependency — uses a plain 2D canvas.
 */

export interface MinimapCameraState {
  x: number;
  y: number;
  scale: number;
}

export class Minimap {
  private canvas: HTMLCanvasElement;
  private worldSize: number;
  private visible = true;

  constructor(container: HTMLElement, worldSize: number) {
    this.worldSize = worldSize;

    const short = Math.min(container.clientWidth, container.clientHeight);
    const size = Math.max(60, Math.min(160, Math.round(short * 0.18)));

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    canvas.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      width: ${size}px;
      height: ${size}px;
      border: 1px solid #1a2a3a;
      background: #060d18;
      border-radius: 4px;
      z-index: 10;
      image-rendering: pixelated;
      pointer-events: none;
    `;
    container.style.position = "relative";
    container.appendChild(canvas);
    this.canvas = canvas;
  }

  show(visible: boolean): void {
    this.visible = visible;
    this.canvas.style.display = visible ? "block" : "none";
  }

  update(camera: MinimapCameraState, screenW: number, screenH: number): void {
    if (!this.visible) return;

    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;

    const mw = this.canvas.width;
    const mh = this.canvas.height;
    ctx.clearRect(0, 0, mw, mh);

    ctx.fillStyle = "#0a1220";
    ctx.fillRect(0, 0, mw, mh);

    const ws = this.worldSize;
    const sx = (-camera.x / camera.scale / ws) * mw;
    const sy = (-camera.y / camera.scale / ws) * mh;
    const sw = (screenW / camera.scale / ws) * mw;
    const sh = (screenH / camera.scale / ws) * mh;

    ctx.strokeStyle = "rgba(240,200,80,0.7)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(sx, sy, sw, sh);
  }

  destroy(): void {
    this.canvas.parentNode?.removeChild(this.canvas);
  }
}
